import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  addLogoToPdf, replaceLogoInPdf, addHeaderFooterToPdf, addWatermarkToPdf,
  addStampToPdf, mergePdfs, reorderPdfPages, deletePdfPages, splitPdf,
  convertToDhrFormat, getPdfInfo, downloadModifiedPdf, replaceTextInPdf, createBusinessCard
} from '../../lib/api';
import {
  FileText, Upload, Download, Loader2, Image, RefreshCw, CheckCircle,
  AlertCircle, Layers, Move, X, FileImage, Plus, Trash2, ArrowUpDown,
  Stamp, Droplet, Type, Building, Hash, GripVertical, FilePlus2, CreditCard, User, Phone, Mail, Briefcase, MousePointer, Scissors
} from 'lucide-react';
import PdfVisualEditor from './PdfVisualEditor';

export default function PdfModifierPage() {
  // Common state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFiles, setPdfFiles] = useState([]); // For merge
  const [logoFile, setLogoFile] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('business-card');
  
  // Business Card state
  const [bcName, setBcName] = useState('');
  const [bcPosition, setBcPosition] = useState('');
  const [bcEmail, setBcEmail] = useState('');
  const [bcPhone, setBcPhone] = useState('');
  const [bcMobile, setBcMobile] = useState('');
  const [bcWebsite, setBcWebsite] = useState('www.dhr-sa.com');
  const [bcTemplate, setBcTemplate] = useState('modern');
  
  // Text Edit state
  const [textFields, setTextFields] = useState([
    { id: 1, x: 50, y: 100, width: 150, height: 20, new_text: '', font_size: 12, label: 'الاسم' },
    { id: 2, x: 50, y: 75, width: 150, height: 15, new_text: '', font_size: 10, label: 'المنصب' },
    { id: 3, x: 50, y: 50, width: 150, height: 12, new_text: '', font_size: 8, label: 'الهاتف' },
    { id: 4, x: 50, y: 35, width: 150, height: 12, new_text: '', font_size: 8, label: 'الإيميل' },
  ]);
  
  // Header/Footer options
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('Distinct High Road (DHR) | www.dhr-sa.com');
  const [addPageNumbers, setAddPageNumbers] = useState(true);
  const [pageNumberPosition, setPageNumberPosition] = useState('bottom-center');
  
  // Watermark options
  const [watermarkText, setWatermarkText] = useState('DHR');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.1);
  const [watermarkAngle, setWatermarkAngle] = useState(45);
  const [watermarkFontSize, setWatermarkFontSize] = useState(60);
  const [watermarkColor, setWatermarkColor] = useState('gray');
  
  // Stamp options
  const [stampType, setStampType] = useState('approved');
  const [stampPosition, setStampPosition] = useState('top-right');
  const [stampPages, setStampPages] = useState('all');
  const [customStampText, setCustomStampText] = useState('');
  
  // Logo options
  const [logoPosition, setLogoPosition] = useState('top-right');
  const [logoPages, setLogoPages] = useState('all');
  const [logoWidth, setLogoWidth] = useState(100);
  const [logoHeight, setLogoHeight] = useState(50);
  
  // Page operations
  const [newPageOrder, setNewPageOrder] = useState('');
  const [pagesToDelete, setPagesToDelete] = useState('');
  
  // Split PDF options
  const [splitType, setSplitType] = useState('all');
  const [splitPageRanges, setSplitPageRanges] = useState('');
  const [splitResults, setSplitResults] = useState([]);
  
  // DHR format options
  const [projectName, setProjectName] = useState('');
  const [documentType, setDocumentType] = useState('General');
  const [dhrHeader, setDhrHeader] = useState(true);
  const [dhrFooter, setDhrFooter] = useState(true);
  const [dhrPageNumbers, setDhrPageNumbers] = useState(true);
  const [dhrWatermark, setDhrWatermark] = useState(false);
  
  // Refs
  const pdfInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const multiPdfInputRef = useRef(null);

  // Handle PDF selection
  const handlePdfSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('يجب أن يكون الملف PDF');
      return;
    }
    
    setPdfFile(file);
    setResult(null);
    
    setLoadingInfo(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', file);
      const response = await getPdfInfo(formData);
      setPdfInfo(response.data);
      
      // Set default page order for reorder tab
      if (response.data.total_pages) {
        setNewPageOrder(Array.from({length: response.data.total_pages}, (_, i) => i + 1).join(','));
      }
    } catch (error) {
      toast.error('فشل قراءة معلومات PDF');
    } finally {
      setLoadingInfo(false);
    }
  };

  // Handle multiple PDF selection for merge
  const handleMultiPdfSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (files.length < 2) {
      toast.error('يجب اختيار ملفين PDF على الأقل للدمج');
      return;
    }
    setPdfFiles(files);
    setResult(null);
  };

  // Handle logo selection
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validExtensions = ['.png', '.jpg', '.jpeg'];
    if (!validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
      toast.error('يجب أن يكون الشعار بصيغة PNG أو JPG');
      return;
    }
    setLogoFile(file);
    setResult(null);
  };

  // Process functions
  const handleHeaderFooter = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      if (logoFile) formData.append('logo_file', logoFile);
      if (headerText) formData.append('header_text', headerText);
      if (footerText) formData.append('footer_text', footerText);
      formData.append('add_page_numbers', addPageNumbers);
      formData.append('page_number_position', pageNumberPosition);
      
      const response = await addHeaderFooterToPdf(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    } finally {
      setProcessing(false);
    }
  };

  const handleWatermark = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('watermark_text', watermarkText);
      formData.append('opacity', watermarkOpacity);
      formData.append('angle', watermarkAngle);
      formData.append('font_size', watermarkFontSize);
      formData.append('color', watermarkColor);
      
      const response = await addWatermarkToPdf(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    } finally {
      setProcessing(false);
    }
  };

  const handleStamp = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('stamp_type', stampType);
      formData.append('position', stampPosition);
      formData.append('page_numbers', stampPages);
      if (stampType === 'custom') formData.append('custom_text', customStampText);
      
      const response = await addStampToPdf(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddLogo = async () => {
    if (!pdfFile || !logoFile) { toast.error('يرجى اختيار ملف PDF والشعار'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('logo_file', logoFile);
      formData.append('position', logoPosition);
      formData.append('page_numbers', logoPages);
      formData.append('logo_width', logoWidth);
      formData.append('logo_height', logoHeight);
      
      const response = await addLogoToPdf(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success('تم إضافة الشعار بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    } finally {
      setProcessing(false);
    }
  };

  const handleMerge = async () => {
    if (pdfFiles.length < 2) { toast.error('يجب اختيار ملفين على الأقل'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      pdfFiles.forEach(file => formData.append('pdf_files', file));
      
      const response = await mergePdfs(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم الدمج بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل الدمج');
    } finally {
      setProcessing(false);
    }
  };

  const handleReorder = async () => {
    if (!pdfFile || !newPageOrder) { toast.error('يرجى اختيار ملف وتحديد الترتيب'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('new_order', newPageOrder);
      
      const response = await reorderPdfPages(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم إعادة الترتيب');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل العملية');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeletePages = async () => {
    if (!pdfFile || !pagesToDelete) { toast.error('يرجى اختيار ملف وتحديد الصفحات'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('pages_to_delete', pagesToDelete);
      
      const response = await deletePdfPages(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم حذف الصفحات');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل الحذف');
    } finally {
      setProcessing(false);
    }
  };

  const handleSplitPdf = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    if ((splitType === 'custom' || splitType === 'ranges') && !splitPageRanges) {
      toast.error('يرجى تحديد أرقام الصفحات');
      return;
    }
    
    setProcessing(true);
    setSplitResults([]);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('split_type', splitType);
      if (splitPageRanges) {
        formData.append('page_ranges', splitPageRanges);
      }
      
      const response = await splitPdf(formData);
      if (response.data.success) {
        setSplitResults(response.data.files || []);
        toast.success(response.data.message_ar || `تم تقسيم الملف إلى ${response.data.total_files} ملفات`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تقسيم الملف');
    } finally {
      setProcessing(false);
    }
  };

  const handleConvertToDhr = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      if (logoFile) formData.append('logo_file', logoFile);
      formData.append('project_name', projectName);
      formData.append('document_type', documentType);
      formData.append('include_header', dhrHeader);
      formData.append('include_footer', dhrFooter);
      formData.append('include_page_numbers', dhrPageNumbers);
      formData.append('include_watermark', dhrWatermark);
      
      const response = await convertToDhrFormat(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم التحويل بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل التحويل');
    } finally {
      setProcessing(false);
    }
  };

  // Create Business Card
  const handleCreateBusinessCard = async () => {
    if (!bcName) { toast.error('يرجى إدخال الاسم'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      if (logoFile) formData.append('logo_file', logoFile);
      formData.append('template_type', bcTemplate);
      formData.append('name', bcName);
      formData.append('position', bcPosition);
      formData.append('email', bcEmail);
      formData.append('phone', bcPhone);
      formData.append('mobile', bcMobile);
      formData.append('website', bcWebsite);
      
      const response = await createBusinessCard(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success('تم إنشاء البزنس كارد بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إنشاء البزنس كارد');
    } finally {
      setProcessing(false);
    }
  };

  // Edit Text in PDF
  const handleEditText = async () => {
    if (!pdfFile) { toast.error('يرجى اختيار ملف PDF'); return; }
    
    const validFields = textFields.filter(f => f.new_text.trim());
    if (validFields.length === 0) { toast.error('يرجى إدخال نص واحد على الأقل'); return; }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('pdf_file', pdfFile);
      formData.append('text_replacements', JSON.stringify(validFields));
      formData.append('page_number', 1);
      
      const response = await replaceTextInPdf(formData);
      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message_ar || 'تم تعديل النصوص بنجاح');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تعديل النصوص');
    } finally {
      setProcessing(false);
    }
  };

  const updateTextField = (id, field, value) => {
    setTextFields(textFields.map(f => f.id === id ? {...f, [field]: value} : f));
  };

  const addTextField = () => {
    const newId = Math.max(...textFields.map(f => f.id)) + 1;
    setTextFields([...textFields, { id: newId, x: 50, y: 20, width: 150, height: 15, new_text: '', font_size: 10, label: `حقل ${newId}` }]);
  };

  const removeTextField = (id) => {
    if (textFields.length > 1) {
      setTextFields(textFields.filter(f => f.id !== id));
    }
  };

  const handleDownload = () => {
    if (!result?.file_name) return;
    window.open(downloadModifiedPdf(result.file_name), '_blank');
  };

  const handleReset = () => {
    setPdfFile(null);
    setPdfFiles([]);
    setLogoFile(null);
    setPdfInfo(null);
    setResult(null);
    if (pdfInputRef.current) pdfInputRef.current.value = '';
    if (logoInputRef.current) logoInputRef.current.value = '';
    if (multiPdfInputRef.current) multiPdfInputRef.current.value = '';
  };

  const positionOptions = [
    { value: 'top-left', label: 'أعلى يسار' },
    { value: 'top-center', label: 'أعلى وسط' },
    { value: 'top-right', label: 'أعلى يمين' },
    { value: 'bottom-left', label: 'أسفل يسار' },
    { value: 'bottom-center', label: 'أسفل وسط' },
    { value: 'bottom-right', label: 'أسفل يمين' },
    { value: 'center', label: 'المنتصف' },
  ];

  const stampTypes = [
    { value: 'approved', label: 'معتمد (Approved)', color: 'bg-green-500' },
    { value: 'rejected', label: 'مرفوض (Rejected)', color: 'bg-red-500' },
    { value: 'confidential', label: 'سري (Confidential)', color: 'bg-red-600' },
    { value: 'draft', label: 'مسودة (Draft)', color: 'bg-gray-500' },
    { value: 'urgent', label: 'عاجل (Urgent)', color: 'bg-orange-500' },
    { value: 'reviewed', label: 'تمت المراجعة (Reviewed)', color: 'bg-blue-500' },
    { value: 'custom', label: 'مخصص (Custom)', color: 'bg-purple-500' },
  ];

  const documentTypes = ['General', 'Quotation', 'Submittal', 'Invoice', 'Contract', 'Report', 'Drawing', 'Manual'];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <FileText className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">تعديل PDF</h1>
            <p className="text-muted-foreground mt-1">PDF Modifier - تعديل شامل لملفات PDF</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleReset} data-testid="reset-pdf-btn">
          <RefreshCw className="h-4 w-4 mr-2" />جديد
        </Button>
      </div>

      {/* Show Visual Editor Full Width for Edit Text Tab */}
      {activeTab === 'edit-text' ? (
        <PdfVisualEditor />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - File Selection */}
        <div className="space-y-4">
          {/* PDF File */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-red-500" />ملف PDF
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <input type="file" ref={pdfInputRef} className="hidden" accept=".pdf" onChange={handlePdfSelect} />
              <Button variant="outline" className="w-full border-dashed" onClick={() => pdfInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" />{pdfFile ? pdfFile.name : 'اختر ملف PDF'}
              </Button>
              
              {loadingInfo && <div className="flex justify-center py-2"><Loader2 className="h-5 w-5 animate-spin" /></div>}
              
              {pdfInfo && (
                <div className="p-2 bg-blue-500/10 rounded text-xs">
                  <p>📄 عدد الصفحات: <strong>{pdfInfo.total_pages}</strong></p>
                  {pdfInfo.pages?.[0] && <p>📐 الأبعاد: {Math.round(pdfInfo.pages[0].width)} × {Math.round(pdfInfo.pages[0].height)}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Logo File */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Image className="h-5 w-5 text-green-500" />الشعار (اختياري)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <input type="file" ref={logoInputRef} className="hidden" accept=".png,.jpg,.jpeg" onChange={handleLogoSelect} />
              <Button variant="outline" className="w-full border-dashed" onClick={() => logoInputRef.current?.click()}>
                <FileImage className="h-4 w-4 mr-2" />{logoFile ? logoFile.name : 'اختر شعار'}
              </Button>
              
              {logoFile && (
                <div className="border rounded p-2 bg-white">
                  <img src={URL.createObjectURL(logoFile)} alt="Logo" className="max-h-16 mx-auto object-contain" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Result */}
          {result && (
            <Card className="bg-green-500/10 border-green-500/20">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">تم بنجاح!</span>
                </div>
                <p className="text-sm text-muted-foreground">{result.message_ar}</p>
                <Button className="w-full" onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" />تحميل الملف
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Main Content - Tabs */}
        <div className="lg:col-span-3">
          <Card className="bg-card border-border">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <CardHeader className="pb-0">
                <TabsList className="grid grid-cols-5 lg:grid-cols-11 gap-1">
                  <TabsTrigger value="business-card" className="text-xs">بزنس كارد</TabsTrigger>
                  <TabsTrigger value="edit-text" className="text-xs">تعديل نص</TabsTrigger>
                  <TabsTrigger value="header-footer" className="text-xs">ترويسة/تذييل</TabsTrigger>
                  <TabsTrigger value="watermark" className="text-xs">علامة مائية</TabsTrigger>
                  <TabsTrigger value="stamp" className="text-xs">ختم</TabsTrigger>
                  <TabsTrigger value="logo" className="text-xs">شعار</TabsTrigger>
                  <TabsTrigger value="merge" className="text-xs">دمج</TabsTrigger>
                  <TabsTrigger value="reorder" className="text-xs">ترتيب</TabsTrigger>
                  <TabsTrigger value="split" className="text-xs">تقسيم</TabsTrigger>
                  <TabsTrigger value="delete" className="text-xs">حذف صفحات</TabsTrigger>
                  <TabsTrigger value="dhr-format" className="text-xs">تنسيق DHR</TabsTrigger>
                </TabsList>
              </CardHeader>
              
              <CardContent className="pt-6">
                {/* Edit Text Tab - Full Visual Editor */}
                <TabsContent value="edit-text" className="mt-0">
                  {/* This content is handled outside the grid layout */}
                </TabsContent>

                {/* Business Card Tab */}
                <TabsContent value="business-card" className="space-y-4 mt-0">
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm text-primary font-medium mb-1">إنشاء بزنس كارد جديد</p>
                    <p className="text-xs text-muted-foreground">أدخل البيانات وسيتم إنشاء بزنس كارد PDF احترافي</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>نوع القالب</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant={bcTemplate === 'modern' ? 'default' : 'outline'} onClick={() => setBcTemplate('modern')} className="h-auto py-3">
                        <div className="text-center">
                          <CreditCard className="h-5 w-5 mx-auto mb-1" />
                          <span className="text-xs">حديث (Modern)</span>
                        </div>
                      </Button>
                      <Button variant={bcTemplate === 'standard' ? 'default' : 'outline'} onClick={() => setBcTemplate('standard')} className="h-auto py-3">
                        <div className="text-center">
                          <CreditCard className="h-5 w-5 mx-auto mb-1" />
                          <span className="text-xs">كلاسيكي (Standard)</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><User className="h-4 w-4" />الاسم *</Label>
                      <Input value={bcName} onChange={(e) => setBcName(e.target.value)} placeholder="محمد أحمد" />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Briefcase className="h-4 w-4" />المنصب</Label>
                      <Input value={bcPosition} onChange={(e) => setBcPosition(e.target.value)} placeholder="مدير المشاريع" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Mail className="h-4 w-4" />البريد الإلكتروني</Label>
                      <Input value={bcEmail} onChange={(e) => setBcEmail(e.target.value)} placeholder="m.ahmed@dhr-sa.com" type="email" />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Phone className="h-4 w-4" />هاتف المكتب</Label>
                      <Input value={bcPhone} onChange={(e) => setBcPhone(e.target.value)} placeholder="+966 11 XXX XXXX" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Phone className="h-4 w-4" />الجوال</Label>
                      <Input value={bcMobile} onChange={(e) => setBcMobile(e.target.value)} placeholder="+966 5X XXX XXXX" />
                    </div>
                    <div className="space-y-2">
                      <Label>الموقع الإلكتروني</Label>
                      <Input value={bcWebsite} onChange={(e) => setBcWebsite(e.target.value)} placeholder="www.dhr-sa.com" />
                    </div>
                  </div>
                  
                  <Button onClick={handleCreateBusinessCard} disabled={!bcName || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    إنشاء البزنس كارد
                  </Button>
                </TabsContent>

                {/* Header/Footer Tab */}
                <TabsContent value="header-footer" className="space-y-4 mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>نص الترويسة (Header)</Label>
                      <Input value={headerText} onChange={(e) => setHeaderText(e.target.value)} placeholder="DISTINCT HIGH ROAD" />
                    </div>
                    <div className="space-y-2">
                      <Label>نص التذييل (Footer)</Label>
                      <Input value={footerText} onChange={(e) => setFooterText(e.target.value)} placeholder="www.dhr-sa.com" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox id="pageNumbers" checked={addPageNumbers} onCheckedChange={setAddPageNumbers} />
                      <Label htmlFor="pageNumbers">إضافة أرقام الصفحات</Label>
                    </div>
                    {addPageNumbers && (
                      <Select value={pageNumberPosition} onValueChange={setPageNumberPosition}>
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {positionOptions.filter(p => p.value.includes('bottom')).map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <Button onClick={handleHeaderFooter} disabled={!pdfFile || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Type className="h-4 w-4 mr-2" />}
                    إضافة الترويسة والتذييل
                  </Button>
                </TabsContent>

                {/* Watermark Tab */}
                <TabsContent value="watermark" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>النص</Label>
                      <Input value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} placeholder="DHR" />
                    </div>
                    <div className="space-y-2">
                      <Label>الشفافية</Label>
                      <Input type="number" value={watermarkOpacity} onChange={(e) => setWatermarkOpacity(parseFloat(e.target.value))} min="0.01" max="1" step="0.05" />
                    </div>
                    <div className="space-y-2">
                      <Label>الزاوية</Label>
                      <Input type="number" value={watermarkAngle} onChange={(e) => setWatermarkAngle(parseInt(e.target.value))} min="-90" max="90" />
                    </div>
                    <div className="space-y-2">
                      <Label>حجم الخط</Label>
                      <Input type="number" value={watermarkFontSize} onChange={(e) => setWatermarkFontSize(parseInt(e.target.value))} min="20" max="200" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>اللون</Label>
                    <div className="flex gap-2">
                      {['gray', 'blue', 'red', 'green'].map(color => (
                        <Button key={color} variant={watermarkColor === color ? 'default' : 'outline'} size="sm" onClick={() => setWatermarkColor(color)}>
                          <span className={`w-4 h-4 rounded-full mr-2 bg-${color}-500`}></span>{color}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleWatermark} disabled={!pdfFile || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Droplet className="h-4 w-4 mr-2" />}
                    إضافة العلامة المائية
                  </Button>
                </TabsContent>

                {/* Stamp Tab */}
                <TabsContent value="stamp" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label>نوع الختم</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {stampTypes.map(stamp => (
                        <Button key={stamp.value} variant={stampType === stamp.value ? 'default' : 'outline'} size="sm" onClick={() => setStampType(stamp.value)} className="justify-start">
                          <span className={`w-3 h-3 rounded-full mr-2 ${stamp.color}`}></span>
                          <span className="truncate">{stamp.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                  {stampType === 'custom' && (
                    <div className="space-y-2">
                      <Label>نص مخصص</Label>
                      <Input value={customStampText} onChange={(e) => setCustomStampText(e.target.value)} placeholder="أدخل النص المخصص" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>موقع الختم</Label>
                      <Select value={stampPosition} onValueChange={setStampPosition}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {positionOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>الصفحات</Label>
                      <Input value={stampPages} onChange={(e) => setStampPages(e.target.value)} placeholder="all أو 1,2,3" />
                    </div>
                  </div>
                  <Button onClick={handleStamp} disabled={!pdfFile || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Stamp className="h-4 w-4 mr-2" />}
                    إضافة الختم
                  </Button>
                </TabsContent>

                {/* Logo Tab */}
                <TabsContent value="logo" className="space-y-4 mt-0">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>الموقع</Label>
                      <Select value={logoPosition} onValueChange={setLogoPosition}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {positionOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>الصفحات</Label>
                      <Input value={logoPages} onChange={(e) => setLogoPages(e.target.value)} placeholder="all أو 1,2,3" />
                    </div>
                    <div className="space-y-2">
                      <Label>العرض</Label>
                      <Input type="number" value={logoWidth} onChange={(e) => setLogoWidth(parseInt(e.target.value))} min="20" max="500" />
                    </div>
                    <div className="space-y-2">
                      <Label>الارتفاع</Label>
                      <Input type="number" value={logoHeight} onChange={(e) => setLogoHeight(parseInt(e.target.value))} min="20" max="500" />
                    </div>
                  </div>
                  <Button onClick={handleAddLogo} disabled={!pdfFile || !logoFile || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Image className="h-4 w-4 mr-2" />}
                    إضافة الشعار
                  </Button>
                </TabsContent>

                {/* Merge Tab */}
                <TabsContent value="merge" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label>اختر ملفات PDF للدمج (ملفين على الأقل)</Label>
                    <input type="file" ref={multiPdfInputRef} className="hidden" accept=".pdf" multiple onChange={handleMultiPdfSelect} />
                    <Button variant="outline" className="w-full border-dashed h-20" onClick={() => multiPdfInputRef.current?.click()}>
                      <div className="text-center">
                        <FilePlus2 className="h-8 w-8 mx-auto mb-2" />
                        <span>{pdfFiles.length > 0 ? `تم اختيار ${pdfFiles.length} ملفات` : 'اختر ملفات PDF'}</span>
                      </div>
                    </Button>
                  </div>
                  {pdfFiles.length > 0 && (
                    <div className="space-y-2">
                      {pdfFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm flex-1">{idx + 1}. {file.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => setPdfFiles(pdfFiles.filter((_, i) => i !== idx))}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button onClick={handleMerge} disabled={pdfFiles.length < 2 || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                    دمج الملفات
                  </Button>
                </TabsContent>

                {/* Reorder Tab */}
                <TabsContent value="reorder" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label>ترتيب الصفحات الجديد</Label>
                    <Input value={newPageOrder} onChange={(e) => setNewPageOrder(e.target.value)} placeholder="1,3,2,5,4" />
                    <p className="text-xs text-muted-foreground">أدخل أرقام الصفحات بالترتيب المطلوب، مفصولة بفاصلة</p>
                  </div>
                  {pdfInfo && (
                    <div className="flex flex-wrap gap-2">
                      {Array.from({length: pdfInfo.total_pages}, (_, i) => (
                        <Badge key={i} variant="outline" className="cursor-pointer hover:bg-primary/10">
                          صفحة {i + 1}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Button onClick={handleReorder} disabled={!pdfFile || !newPageOrder || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowUpDown className="h-4 w-4 mr-2" />}
                    إعادة ترتيب الصفحات
                  </Button>
                </TabsContent>

                {/* Delete Pages Tab */}
                <TabsContent value="delete" className="space-y-4 mt-0">
                  <div className="space-y-2">
                    <Label>الصفحات المراد حذفها</Label>
                    <Input value={pagesToDelete} onChange={(e) => setPagesToDelete(e.target.value)} placeholder="1,3,5" />
                    <p className="text-xs text-muted-foreground">أدخل أرقام الصفحات المراد حذفها، مفصولة بفاصلة</p>
                  </div>
                  {pdfInfo && (
                    <div className="flex flex-wrap gap-2">
                      {Array.from({length: pdfInfo.total_pages}, (_, i) => {
                        const isSelected = pagesToDelete.split(',').map(p => parseInt(p.trim())).includes(i + 1);
                        return (
                          <Badge key={i} variant={isSelected ? "destructive" : "outline"} className="cursor-pointer" onClick={() => {
                            const pages = pagesToDelete.split(',').map(p => p.trim()).filter(p => p);
                            const pageNum = (i + 1).toString();
                            if (pages.includes(pageNum)) {
                              setPagesToDelete(pages.filter(p => p !== pageNum).join(','));
                            } else {
                              setPagesToDelete([...pages, pageNum].join(','));
                            }
                          }}>
                            صفحة {i + 1}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  <Button onClick={handleDeletePages} disabled={!pdfFile || !pagesToDelete || processing} variant="destructive" className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    حذف الصفحات المحددة
                  </Button>
                </TabsContent>

                {/* Split PDF Tab */}
                <TabsContent value="split" className="space-y-4 mt-0">
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm text-primary font-medium mb-2">تقسيم ملف PDF</p>
                    <p className="text-xs text-muted-foreground">قسّم ملف PDF إلى عدة ملفات منفصلة</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>طريقة التقسيم</Label>
                    <Select value={splitType} onValueChange={(value) => { setSplitType(value); setSplitPageRanges(''); setSplitResults([]); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">تقسيم كل صفحة بشكل منفصل</SelectItem>
                        <SelectItem value="half">تقسيم إلى نصفين</SelectItem>
                        <SelectItem value="custom">تقسيم عند صفحة محددة</SelectItem>
                        <SelectItem value="ranges">تقسيم بنطاقات مخصصة</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {splitType === 'custom' && (
                    <div className="space-y-2">
                      <Label>رقم صفحة التقسيم</Label>
                      <Input 
                        value={splitPageRanges} 
                        onChange={(e) => setSplitPageRanges(e.target.value)} 
                        placeholder="مثال: 5 (للتقسيم بعد الصفحة 5)"
                        type="number"
                        min="1"
                        max={pdfInfo?.total_pages ? pdfInfo.total_pages - 1 : 100}
                      />
                      <p className="text-xs text-muted-foreground">
                        سيتم تقسيم الملف بعد هذه الصفحة (الجزء الأول: صفحات 1-{splitPageRanges || 'X'}، الجزء الثاني: الباقي)
                      </p>
                    </div>
                  )}
                  
                  {splitType === 'ranges' && (
                    <div className="space-y-2">
                      <Label>نطاقات الصفحات</Label>
                      <Input 
                        value={splitPageRanges} 
                        onChange={(e) => setSplitPageRanges(e.target.value)} 
                        placeholder="مثال: 1-3,4-6,7-10"
                      />
                      <p className="text-xs text-muted-foreground">
                        أدخل نطاقات الصفحات مفصولة بفاصلة. مثال: 1-3,4-6,7-10 سينتج 3 ملفات
                      </p>
                    </div>
                  )}
                  
                  {pdfInfo && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">
                        <span className="font-medium">عدد صفحات الملف:</span> {pdfInfo.total_pages} صفحة
                      </p>
                      {splitType === 'all' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          سيتم إنشاء {pdfInfo.total_pages} ملف منفصل (ملف لكل صفحة)
                        </p>
                      )}
                      {splitType === 'half' && (
                        <p className="text-xs text-muted-foreground mt-1">
                          سيتم إنشاء ملفين: الأول ({Math.floor(pdfInfo.total_pages/2)} صفحة) والثاني ({pdfInfo.total_pages - Math.floor(pdfInfo.total_pages/2)} صفحة)
                        </p>
                      )}
                    </div>
                  )}
                  
                  <Button onClick={handleSplitPdf} disabled={!pdfFile || processing || ((splitType === 'custom' || splitType === 'ranges') && !splitPageRanges)} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                    تقسيم الملف
                  </Button>
                  
                  {/* Split Results */}
                  {splitResults.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <Label className="text-green-600 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        تم إنشاء {splitResults.length} ملفات:
                      </Label>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {splitResults.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-primary" />
                              <div>
                                <p className="text-sm font-medium">{file.file_name}</p>
                                <p className="text-xs text-muted-foreground">{file.pages} ({file.page_count} صفحة)</p>
                              </div>
                            </div>
                            <a
                              href={downloadModifiedPdf(file.file_name)}
                              download
                              className="flex items-center gap-1 text-sm text-primary hover:underline"
                            >
                              <Download className="h-4 w-4" />
                              تحميل
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* DHR Format Tab */}
                <TabsContent value="dhr-format" className="space-y-4 mt-0">
                  <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                    <p className="text-sm text-primary font-medium mb-2">تحويل إلى تنسيق DHR الموحد</p>
                    <p className="text-xs text-muted-foreground">يضيف ترويسة وتذييل DHR احترافية مع شعار الشركة ومعلومات الاتصال</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>اسم المشروع</Label>
                      <Input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="مشروع برج الرياض" />
                    </div>
                    <div className="space-y-2">
                      <Label>نوع المستند</Label>
                      <Select value={documentType} onValueChange={setDocumentType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {documentTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox id="dhrHeader" checked={dhrHeader} onCheckedChange={setDhrHeader} />
                      <Label htmlFor="dhrHeader" className="text-sm">ترويسة DHR</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="dhrFooter" checked={dhrFooter} onCheckedChange={setDhrFooter} />
                      <Label htmlFor="dhrFooter" className="text-sm">تذييل DHR</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="dhrPageNum" checked={dhrPageNumbers} onCheckedChange={setDhrPageNumbers} />
                      <Label htmlFor="dhrPageNum" className="text-sm">أرقام الصفحات</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="dhrWatermark" checked={dhrWatermark} onCheckedChange={setDhrWatermark} />
                      <Label htmlFor="dhrWatermark" className="text-sm">علامة مائية</Label>
                    </div>
                  </div>
                  <Button onClick={handleConvertToDhr} disabled={!pdfFile || processing} className="w-full">
                    {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Building className="h-4 w-4 mr-2" />}
                    تحويل إلى تنسيق DHR
                  </Button>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
